import React, { useState, useEffect } from "react";
import MintDBLoader from "./MintDBLoader.tsx";

interface InvestigationalTarget {
  drug_name: string;
  gene_name: string;
  target_name: string;
  uniprot_id: string | null;
  pdb_id: string | null;
  is_gpcr: boolean;
  disorder_name: string;
}

interface Mutation {
  rsid: string;
  mutation: string;
  position: number;
  original_aa: string;
  mutated_aa: string;
  classification?: string;
  uk_biobank?: string;
}

interface MutationData {
  existing: Mutation[];
  investigational: Record<string, Mutation[]>;
}

interface DrugGroup {
  drug_name: string;
  disorder_name: string;
  targets: InvestigationalTarget[];
}

const classificationColors: Record<string, { bg: string; text: string }> = {
  pathogenic: { bg: "#fef2f2", text: "#dc2626" },
  "likely pathogenic": { bg: "#fef2f2", text: "#dc2626" },
  benign: { bg: "#f0fdf4", text: "#16a34a" },
  "likely benign": { bg: "#f0fdf4", text: "#16a34a" },
  ambiguous: { bg: "#fefce8", text: "#ca8a04" },
};

function InvestigationalTargets() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drugGroups, setDrugGroups] = useState<DrugGroup[]>([]);
  const [selectedDrug, setSelectedDrug] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mutations, setMutations] = useState<Record<string, MutationData>>({});
  const [mutLoading, setMutLoading] = useState<Record<string, boolean>>({});
  const [expandedGene, setExpandedGene] = useState<string | null>(null);
  const [expandedSection, setExpandedSection] = useState<Record<string, string | null>>({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch("/api/investigational-targets");
        if (!response.ok) {
          throw new Error("Failed to fetch investigational targets");
        }
        const data: InvestigationalTarget[] = await response.json();

        const grouped = new Map<string, DrugGroup>();
        for (const row of data) {
          if (!grouped.has(row.drug_name)) {
            grouped.set(row.drug_name, {
              drug_name: row.drug_name,
              disorder_name: row.disorder_name,
              targets: [],
            });
          }
          grouped.get(row.drug_name)!.targets.push(row);
        }
        const groups = Array.from(grouped.values());
        setDrugGroups(groups);
        if (groups.length > 0) {
          setSelectedDrug(groups[0].drug_name);
        }
      } catch (err: any) {
        console.error("Error fetching investigational targets:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const fetchMutations = async (geneName: string) => {
    if (mutations[geneName]) return;
    setMutLoading((prev) => ({ ...prev, [geneName]: true }));
    try {
      const resp = await fetch(`/api/investigational-targets?gene=${encodeURIComponent(geneName)}`);
      const data = await resp.json();
      setMutations((prev) => ({
        ...prev,
        [geneName]: {
          existing: Array.isArray(data.existing) ? data.existing : [],
          investigational: (data.investigational && typeof data.investigational === "object") ? data.investigational : {},
        }
      }));
    } catch {
      setMutations((prev) => ({ ...prev, [geneName]: { existing: [], investigational: {} } }));
    } finally {
      setMutLoading((prev) => ({ ...prev, [geneName]: false }));
    }
  };

  const toggleGene = (geneName: string) => {
    if (expandedGene === geneName) {
      setExpandedGene(null);
    } else {
      setExpandedGene(geneName);
      fetchMutations(geneName);
    }
  };

  if (loading) {
    return <MintDBLoader />;
  }

  if (error) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: "#FBFBFC", paddingBottom: "3rem" }}>
        <div className="container" style={{ paddingTop: "4rem", textAlign: "center" }}>
          <h2 style={{ color: "#1A2940" }}>Error</h2>
          <p style={{ color: "#64748b" }}>{error}</p>
        </div>
      </div>
    );
  }

  const selected = drugGroups.find((g) => g.drug_name === selectedDrug);

  const renderMutationTable = (muts: Mutation[], showClassification: boolean) => {
    const headers = showClassification
      ? ["RSID", "Amino Acid Change", "Position", "Classification", "Biobank"]
      : ["RSID", "Amino Acid Change", "Position"];

    return (
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h} style={{
                padding: "0.375rem 0.5rem",
                textAlign: h === "Position" ? "center" : "left",
                color: "#64748b",
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                fontSize: "0.6875rem",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {muts.map((m, idx) => {
            const cls = m.classification?.toLowerCase() || "";
            const colors = classificationColors[cls] || { bg: "#f8fafc", text: "#64748b" };
            return (
              <tr key={idx}>
                <td style={{
                  padding: "0.375rem 0.5rem",
                  color: "#0f172a",
                  fontWeight: 500,
                  fontFamily: "monospace",
                  borderBottom: "1px solid #f1f5f9",
                }}>{m.rsid}</td>
                <td style={{
                  padding: "0.375rem 0.5rem",
                  color: "#475569",
                  borderBottom: "1px solid #f1f5f9",
                }}>{m.mutation || `${m.original_aa}\u2009\u2192\u2009${m.mutated_aa}`}</td>
                <td style={{
                  padding: "0.375rem 0.5rem",
                  textAlign: "center",
                  color: "#0f172a",
                  fontFamily: "monospace",
                  borderBottom: "1px solid #f1f5f9",
                }}>{m.position}</td>
                {showClassification && (
                  <>
                    <td style={{ padding: "0.375rem 0.5rem", borderBottom: "1px solid #f1f5f9" }}>
                      <span style={{
                        display: "inline-block",
                        padding: "0.1rem 0.4rem",
                        borderRadius: "3px",
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        backgroundColor: colors.bg,
                        color: colors.text,
                        textTransform: "capitalize",
                      }}>{m.classification || "Unknown"}</span>
                    </td>
                    <td style={{ padding: "0.375rem 0.5rem", borderBottom: "1px solid #f1f5f9" }}>
                      {m.uk_biobank === "yes" ? (
                        <span style={{
                          display: "inline-block",
                          padding: "0.1rem 0.4rem",
                          borderRadius: "3px",
                          fontSize: "0.6875rem",
                          fontWeight: 600,
                          backgroundColor: "#dbeafe",
                          color: "#1e40af",
                        }}>Yes</span>
                      ) : (
                        <span style={{ color: "#cbd5e1" }}>No</span>
                      )}
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <style>{`
        .inv-layout {
          display: flex;
          height: calc(100vh - 80px);
          overflow: hidden;
        }

        .inv-sidebar {
          width: 260px;
          min-width: 260px;
          background: #f9fafb;
          border-right: 1px solid #e5e7eb;
          height: 100%;
          overflow-y: auto;
          overflow-x: hidden;
          flex-shrink: 0;
        }

        .inv-sidebar::-webkit-scrollbar {
          width: 3px;
        }
        .inv-sidebar::-webkit-scrollbar-track {
          background: transparent;
        }
        .inv-sidebar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 2px;
        }

        .inv-sidebar-header {
          padding: 1.25rem 0.875rem 1rem;
          border-bottom: 2px solid #60a5fa;
          background: #f0f4f8;
        }

        .inv-sidebar-header h2 {
          font-size: 1.1rem;
          font-weight: 800;
          color: #1A2940;
          margin: 0 0 0.25rem;
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }

        .inv-sidebar-header p {
          font-size: 0.75rem;
          color: #64748b;
          margin: 0;
        }

        .inv-drug-list {
          padding: 0.25rem 0;
        }

        .inv-drug-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.6rem 0.875rem;
          cursor: pointer;
          transition: all 0.12s ease;
          border-left: 2px solid transparent;
        }

        .inv-drug-item:hover {
          background: #f0f4f8;
          border-left-color: #cbd5e1;
        }

        .inv-drug-item.active {
          background: #eef2ff;
          border-left-color: #60a5fa;
        }

        .inv-drug-item-info {
          flex: 1;
          min-width: 0;
        }

        .inv-drug-badge {
          display: inline-block;
          background: #1A2940;
          color: #fff;
          font-size: 0.65rem;
          font-weight: 600;
          padding: 0.1rem 0.4rem;
          border-radius: 3px;
          letter-spacing: 0.03em;
          line-height: 1.2;
        }

        .inv-drug-item.active .inv-drug-badge {
          background: #2563eb;
        }

        .inv-drug-disorder {
          font-size: 0.6875rem;
          color: #64748b;
          line-height: 1.25;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-top: 0.125rem;
        }

        .inv-drug-item.active .inv-drug-disorder {
          color: #1e293b;
        }

        .inv-target-count {
          font-size: 0.625rem;
          color: #cbd5e1;
          flex-shrink: 0;
        }

        .inv-drug-item.active .inv-target-count {
          color: #3b82f6;
          font-weight: 600;
        }

        .inv-main {
          flex: 1;
          min-width: 0;
          background: #fff;
          overflow-y: auto;
        }

        .inv-content {
          padding: 1.5rem;
        }

        .inv-detail-header h1 {
          color: #0f172a;
          font-size: 1.375rem;
          font-weight: 700;
          letter-spacing: -0.01em;
          margin: 0;
        }

        .inv-detail-header .subtitle {
          color: #64748b;
          font-size: 0.8125rem;
        }

        .inv-target-card {
          border: 1px solid #e2e8f0;
          border-radius: 0.5rem;
          margin-bottom: 1rem;
          overflow: hidden;
        }

        .inv-target-card-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          background: #f8fafc;
          cursor: pointer;
          transition: background 0.12s ease;
          user-select: none;
        }

        .inv-target-card-header:hover {
          background: #f0f4f8;
        }

        .inv-target-card-header.expanded {
          border-bottom: 1px solid #e2e8f0;
        }

        .inv-chevron {
          font-size: 0.7rem;
          color: #94a3b8;
          transition: transform 0.2s ease;
          flex-shrink: 0;
        }

        .inv-chevron.expanded {
          transform: rotate(90deg);
        }

        .inv-mobile-toggle {
          display: none;
          position: fixed;
          bottom: 1rem;
          left: 1rem;
          z-index: 1050;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #1A2940;
          color: #fff;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2);
          font-size: 1rem;
          cursor: pointer;
          align-items: center;
          justify-content: center;
        }

        .inv-overlay {
          display: none;
        }

        @media (max-width: 991px) {
          .inv-sidebar {
            position: fixed;
            left: 0;
            top: 0;
            z-index: 1040;
            transform: translateX(-100%);
            transition: transform 0.25s ease;
            height: 100vh;
          }

          .inv-sidebar.open {
            transform: translateX(0);
          }

          .inv-mobile-toggle {
            display: flex;
          }

          .inv-overlay {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0,0,0,0.25);
            z-index: 1035;
          }

          .inv-overlay.open {
            display: block;
          }

          .inv-content {
            padding: 1rem;
          }
        }
      `}</style>

      {/* Mobile sidebar overlay */}
      <div
        className={`inv-overlay ${sidebarOpen ? "open" : ""}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Mobile sidebar toggle */}
      <button
        className="inv-mobile-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        title="Toggle drug list"
      >
        {sidebarOpen ? "\u2715" : "\u2630"}
      </button>

      <div className="inv-layout">
        {/* Left Sidebar */}
        <aside className={`inv-sidebar ${sidebarOpen ? "open" : ""}`}>
          <div className="inv-sidebar-header">
            <h2>Investigational</h2>
            <p>{drugGroups.length} drug{drugGroups.length !== 1 ? "s" : ""}</p>
          </div>

          <div className="inv-drug-list">
            {drugGroups.map((group) => (
              <div
                key={group.drug_name}
                className={`inv-drug-item ${selectedDrug === group.drug_name ? "active" : ""}`}
                onClick={() => {
                  setSelectedDrug(group.drug_name);
                  setExpandedGene(null);
                  setSidebarOpen(false);
                }}
              >
                <div className="inv-drug-item-info">
                  <span className="inv-drug-badge">{group.drug_name}</span>
                  <div className="inv-drug-disorder" title={group.disorder_name}>
                    {group.disorder_name}
                  </div>
                </div>

                <span className="inv-target-count">
                  {group.targets.length} target{group.targets.length !== 1 ? "s" : ""}
                </span>
              </div>
            ))}
          </div>
        </aside>

        {/* Main Content */}
        <main className="inv-main">
          {selected ? (
            <div className="inv-content">
              <div className="inv-detail-header" style={{ marginBottom: "1.25rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                  <h1>{selected.drug_name}</h1>
                  <span className="subtitle">{selected.disorder_name}</span>
                </div>
                <p style={{ color: "#64748b", fontSize: "0.8125rem", margin: "0.25rem 0 0" }}>
                  {selected.targets.length} target{selected.targets.length !== 1 ? "s" : ""} — click a target to view mutations
                </p>
              </div>

              {/* Target Cards */}
              {selected.targets.map((t) => {
                const isExpanded = expandedGene === t.gene_name;
                const geneMuts = mutations[t.gene_name];
                const isLoading = mutLoading[t.gene_name];

                return (
                  <div key={t.gene_name} className="inv-target-card">
                    {/* Card Header */}
                    <div
                      className={`inv-target-card-header ${isExpanded ? "expanded" : ""}`}
                      onClick={() => toggleGene(t.gene_name)}
                    >
                      <span className={`inv-chevron ${isExpanded ? "expanded" : ""}`}>&#9654;</span>

                      <span style={{
                        display: "inline-block",
                        background: "#1A2940",
                        color: "#fff",
                        fontSize: "0.625rem",
                        fontWeight: 600,
                        padding: "0.1rem 0.375rem",
                        borderRadius: "3px",
                        letterSpacing: "0.03em",
                      }}>{t.gene_name}</span>

                      <span style={{ color: "#475569", fontSize: "0.8125rem", flex: 1 }}>
                        {t.target_name}
                      </span>

                      {t.is_gpcr && (
                        <span style={{
                          backgroundColor: "#7c3aed",
                          color: "white",
                          fontSize: "0.625rem",
                          fontWeight: 600,
                          padding: "0.15rem 0.375rem",
                          borderRadius: "3px",
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}>GPCR</span>
                      )}

                      <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                        {t.uniprot_id && (
                          <a
                            href={`https://www.uniprot.org/uniprot/${t.uniprot_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              backgroundColor: "#dbeafe",
                              color: "#1e40af",
                              fontWeight: 600,
                              fontSize: "0.6875rem",
                              padding: "0.1rem 0.375rem",
                              borderRadius: "3px",
                              textDecoration: "none",
                            }}
                          >
                            {t.uniprot_id}
                          </a>
                        )}
                        {t.pdb_id && (
                          <a
                            href={`https://www.rcsb.org/structure/${t.pdb_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              color: "#3b82f6",
                              fontSize: "0.6875rem",
                              fontWeight: 500,
                              textDecoration: "none",
                            }}
                          >
                            {t.pdb_id}
                          </a>
                        )}
                      </div>

                      {geneMuts && !isLoading && (() => {
                        const invTotal = Object.values(geneMuts.investigational).reduce((sum, arr) => sum + arr.length, 0);
                        const total = geneMuts.existing.length + invTotal;
                        return (
                          <span style={{ fontSize: "0.625rem", color: "#94a3b8", flexShrink: 0 }}>
                            {total} mutation{total !== 1 ? "s" : ""}
                          </span>
                        );
                      })()}
                    </div>

                    {/* Expanded Mutations */}
                    {isExpanded && (
                      <div style={{ padding: "0.75rem 1rem" }}>
                        {isLoading ? (
                          <div style={{ textAlign: "center", padding: "1rem", color: "#94a3b8", fontSize: "0.8125rem" }}>
                            Loading mutations...
                          </div>
                        ) : geneMuts && (geneMuts.existing.length > 0 || Object.keys(geneMuts.investigational).length > 0) ? (
                          <>
                            {/* Existing Mutations Section */}
                            {geneMuts.existing.length > 0 && (
                              <div style={{ marginBottom: Object.keys(geneMuts.investigational).length > 0 ? "0.75rem" : 0 }}>
                                <div
                                  onClick={() => setExpandedSection((prev) => ({
                                    ...prev,
                                    [`${t.gene_name}_existing`]: prev[`${t.gene_name}_existing`] === "open" ? null : "open"
                                  }))}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "0.5rem 0.625rem",
                                    background: "#f0f4f8",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: "0.375rem",
                                    cursor: "pointer",
                                    userSelect: "none",
                                  }}
                                >
                                  <span style={{
                                    fontSize: "0.6rem",
                                    color: "#64748b",
                                    transition: "transform 0.2s ease",
                                    transform: expandedSection[`${t.gene_name}_existing`] === "open" ? "rotate(90deg)" : "none",
                                  }}>&#9654;</span>
                                  <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#1A2940", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Existing Mutations
                                  </span>
                                  <span style={{ fontSize: "0.6875rem", color: "#64748b" }}>
                                    ({geneMuts.existing.length})
                                  </span>
                                </div>
                                {expandedSection[`${t.gene_name}_existing`] === "open" && (
                                  <div style={{ overflowX: "auto", marginTop: "0.5rem" }}>
                                    {renderMutationTable(geneMuts.existing, true)}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Investigational Mutations Section */}
                            {Object.keys(geneMuts.investigational).length > 0 && (
                              <div>
                                <div
                                  onClick={() => setExpandedSection((prev) => ({
                                    ...prev,
                                    [`${t.gene_name}_inv`]: prev[`${t.gene_name}_inv`] === "open" ? null : "open"
                                  }))}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "0.5rem",
                                    padding: "0.5rem 0.625rem",
                                    background: "#eef2ff",
                                    border: "1px solid #c7d2fe",
                                    borderRadius: "0.375rem",
                                    cursor: "pointer",
                                    userSelect: "none",
                                  }}
                                >
                                  <span style={{
                                    fontSize: "0.6rem",
                                    color: "#64748b",
                                    transition: "transform 0.2s ease",
                                    transform: expandedSection[`${t.gene_name}_inv`] === "open" ? "rotate(90deg)" : "none",
                                  }}>&#9654;</span>
                                  <span style={{ fontSize: "0.75rem", fontWeight: 700, color: "#3730a3", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Investigational Mutations
                                  </span>
                                  <span style={{ fontSize: "0.6875rem", color: "#64748b" }}>
                                    ({Object.values(geneMuts.investigational).reduce((sum, arr) => sum + arr.length, 0)})
                                  </span>
                                </div>
                                {expandedSection[`${t.gene_name}_inv`] === "open" && (
                                  <div style={{ padding: "0.5rem 0 0 1rem" }}>
                                    {Object.entries(geneMuts.investigational).map(([disorder, muts]) => (
                                      <div key={disorder} style={{ marginBottom: "0.5rem" }}>
                                        <div
                                          onClick={() => setExpandedSection((prev) => ({
                                            ...prev,
                                            [`${t.gene_name}_inv_${disorder}`]: prev[`${t.gene_name}_inv_${disorder}`] === "open" ? null : "open"
                                          }))}
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "0.5rem",
                                            padding: "0.4rem 0.5rem",
                                            background: "#f5f3ff",
                                            border: "1px solid #ddd6fe",
                                            borderRadius: "0.25rem",
                                            cursor: "pointer",
                                            userSelect: "none",
                                          }}
                                        >
                                          <span style={{
                                            fontSize: "0.55rem",
                                            color: "#7c3aed",
                                            transition: "transform 0.2s ease",
                                            transform: expandedSection[`${t.gene_name}_inv_${disorder}`] === "open" ? "rotate(90deg)" : "none",
                                          }}>&#9654;</span>
                                          <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "#5b21b6" }}>
                                            {disorder}
                                          </span>
                                          <span style={{ fontSize: "0.6875rem", color: "#8b5cf6" }}>
                                            ({muts.length})
                                          </span>
                                        </div>
                                        {expandedSection[`${t.gene_name}_inv_${disorder}`] === "open" && (
                                          <div style={{ overflowX: "auto", marginTop: "0.375rem" }}>
                                            {renderMutationTable(muts, false)}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{ textAlign: "center", padding: "1rem", color: "#94a3b8", fontSize: "0.8125rem", fontStyle: "italic" }}>
                            No mutations found for this target
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
              <p style={{ color: "#6b7280" }}>Select a drug from the sidebar</p>
            </div>
          )}
        </main>
      </div>
    </>
  );
}

export default InvestigationalTargets;
